import type {
  LocalMediaAdapter,
  MediaLibraryItem,
  MediaLibraryLoadParams,
} from '@reactvideoeditor/react-video-editor/types';
import {
  listAssetsForOrg,
  uploadAssetWithProgress,
  type UploadAssetParams,
} from '@/services/assets';
import type { Asset } from '@/types/api';
import {
  assetToLibraryItem,
} from '@/rve/aspectlyLocalMediaMapping';
import { assetContentUrl, resolveAssetThumbnailUrl } from '@/rve/orgMediaUrls';

const PAGE_SIZE = 50;

export interface AspectlyLocalMediaAdaptorDeps {
  listAssetsForOrg: typeof listAssetsForOrg;
  uploadAssetWithProgress: (
    params: UploadAssetParams,
    onProgress?: (progress: number) => void,
  ) => Promise<Asset>;
}

const defaultDeps: AspectlyLocalMediaAdaptorDeps = {
  listAssetsForOrg,
  uploadAssetWithProgress,
};

/**
 * RVE My Library adaptor backed by Aspectly org assets (D14: fixed creative org).
 *
 * Upload uses existing multipart/resumable asset APIs. No delete (Q5).
 */
export function createAspectlyLocalMediaAdaptor(
  orgId: number,
  deps: AspectlyLocalMediaAdaptorDeps = defaultDeps,
): LocalMediaAdapter {
  return {
    async load(params: MediaLibraryLoadParams) {
      if (params.type === 'audio') {
        return { items: [] };
      }

      const page = await deps.listAssetsForOrg(
        orgId,
        params.cursor,
        Math.max(params.limit, PAGE_SIZE),
      );
      const rows = page.items;
      const items = rows
        .map((row) => assetToLibraryItem(orgId, row))
        .filter((row): row is MediaLibraryItem => row !== null)
        .filter((row) => row.type === params.type)
        .slice(0, params.limit);

      const nextCursor = page.next_cursor ?? undefined;

      return { items, nextCursor };
    },

    async upload(file: File) {
      const asset = await deps.uploadAssetWithProgress({ file, name: file.name });
      if (asset.id == null) {
        throw new Error('Upload succeeded but asset id is missing');
      }
      return {
        url: assetContentUrl(orgId, asset.id),
        name: asset.name,
        thumbnail: resolveAssetThumbnailUrl(orgId, asset),
      };
    },
  };
}
